// OwnerSettingsSalaryRowTypeList - MODULE
// ***********************************************************************************************************************
import * as React from "react";
import * as store from "../../framework/customStore";
import * as baseService from "../../services/baseService";
import * as salaryRowTypeService from "../../services/salaryRowTypeService";
import { ToolButton } from "../framework/toolButton";
import { Base } from "../../framework/base";
import { ListSearchFilter } from "../framework/listSearchFilter";
import { ListHeaderColumn } from "../framework/listHeaderColumn";
import { ToolTitle } from "../framework/toolTitle";
import * as StoreActions from "../../models/store/storeActions";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import { infiniteListPageSize } from "../../models/common/consts";
import { handleApiError } from "../../models/store/storeEffects";
import { SalaryRowTypeItem, ISalaryRowTypeItem } from "../../models/salary/salaryRowTypeItem";
import { ISalaryRowTypeEdit } from "../../models/salary/salaryRowTypeEdit";
import { OwnerSettingsSalaryRowTypeDialog } from "./ownerSettingsSalaryRowTypeDialog";
import { t } from "i18next";

// OwnerSettingsSalaryRowTypeListLineHeader
export interface IOwnerSettingsSalaryRowTypeListLineHeaderProp {
    sortColumn: string;
    sortOrderIsAsc: boolean;
    onColumnClick: (column: string) => void;
}

export class OwnerSettingsSalaryRowTypeListLineHeader extends React.Component<IOwnerSettingsSalaryRowTypeListLineHeaderProp, {}> {
    render() {
        const props = this.props;
        return (
            <div className="row title">
                <ListHeaderColumn
                    title={t("common.code")}
                    column="code"
                    classes="col-2"
                    sortColumn={props.sortColumn}
                    sortOrderIsAsc={props.sortOrderIsAsc}
                    onClick={props.onColumnClick}
                />
                <ListHeaderColumn
                    title={t("common.name")}
                    column="name"
                    classes="col-4"
                    sortColumn={props.sortColumn}
                    sortOrderIsAsc={props.sortOrderIsAsc}
                    onClick={props.onColumnClick}
                />
                <ListHeaderColumn
                    title={t("hourBooking.categoryShort")}
                    column="hourBookingCategoryNames"
                    classes="col-6"
                    sortColumn={props.sortColumn}
                    sortOrderIsAsc={props.sortOrderIsAsc}
                    onClick={props.onColumnClick}
                />
            </div>
        );
    }
}

// OwnerSettingsSalaryRowTypeListLine
export interface IOwnerSettingsSalaryRowTypeListLineProp {
    item: ISalaryRowTypeItem;
    selectedId: string;
    onClick: (id: string) => void;
    onDoubleClick: (id: string) => void;
}

export class OwnerSettingsSalaryRowTypeListLine extends React.Component<IOwnerSettingsSalaryRowTypeListLineProp, {}> {
    handleOpenClick = (e) => {
        this.props.onDoubleClick(this.props.item.id);
    };

    render() {
        const props = this.props;
        const item = props.item;
        return (
            <div className={"row line" + (item.id === props.selectedId ? " selected" : "")} onClick={() => { props.onClick(item.id); }} onDoubleClick={this.handleOpenClick} >
                <div className="col-2 ca openOnClick" onClick={this.handleOpenClick}>{item.code}</div>
                <div className="col-4 text-truncate">{item.name}</div>
                <div className="col-6 text-truncate">{item.hourBookingCategories.map(c => t(`hourBookingCategory.${c}`)).join(", ")}</div>
            </div>
        );
    }
}

// OwnerSettingsSalaryRowTypeList
export interface IOwnerSettingsSalaryRowTypeListProp {
}

interface IOwnerSettingsSalaryRowTypeListState {
    isLoading: boolean;

    // List
    pageSize: number;
    page: number;
    hasMore: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    items: ISalaryRowTypeItem[];
    selectedId: string;
    checkedIds: string[];

    // Edit
    edit: ISalaryRowTypeEdit;
    showEditDialog: boolean;
}

export class OwnerSettingsSalaryRowTypeList extends React.Component<IOwnerSettingsSalaryRowTypeListProp, IOwnerSettingsSalaryRowTypeListState> {
    private containerDiv: HTMLDivElement;
    private listDiv: HTMLDivElement;

    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            pageSize: infiniteListPageSize,
            page: 1,
            hasMore: false,
            filter: "",
            sortColumn: "name",
            sortOrderIsAsc: true,
            items: [],
            selectedId: null,
            checkedIds: [],
            showEditDialog: false,
            edit: null
        };
    }

    searchItems = (pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean, successCallback: () => void = null) => {
        const obj = this;
        const state = this.state;
        this.setState({
            isLoading: true
        });
        store.customStore.dispatch(StoreActions.fetchStart());
        salaryRowTypeService.getSalaryRowTypeItems(!refreshList ? pageSize : (page * infiniteListPageSize), !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc).then(dbItems => {
            const listItems = Base.getListItems<SalaryRowTypeItem>(state.items, state.selectedId, state.checkedIds, dbItems.items, resetItems, refreshList);
            obj.setState({
                page: !refreshList ? dbItems.page : page,
                hasMore: dbItems.hasMore,
                filter: filter,
                sortColumn: sortColumn,
                sortOrderIsAsc: sortOrderIsAsc,
                items: listItems.items,
                selectedId: listItems.selectedId,
                checkedIds: listItems.checkedIds,
            });
            if (successCallback) {
                successCallback();
            }
        },
        error => {
            handleApiError(error, store.customStore.dispatch);
        }).finally(() => {
            obj.setState({
                isLoading: false
            });
            store.customStore.dispatch(StoreActions.fetchEnd());
        });
    };

    refreshList = () => {
        this.searchItems(this.state.pageSize, this.state.page, this.state.filter, this.state.sortColumn, this.state.sortOrderIsAsc, false, true);
    };

    handleScrollSub = Base.debounce((obj: OwnerSettingsSalaryRowTypeList) => {
        if (obj.state.isLoading || !obj.state.hasMore) return;
        if (obj.listDiv.offsetHeight - (obj.containerDiv.clientHeight + obj.containerDiv.scrollTop) < 5) {
            obj.searchItems(obj.state.pageSize, obj.state.page + 1, obj.state.filter, obj.state.sortColumn, obj.state.sortOrderIsAsc, false, false);
        }
    }, 100);

    handleScroll = (event) => {
        this.handleScrollSub(this);
    };

    componentDidMount(): void {
        const state = this.state;
        this.containerDiv.addEventListener("scroll", this.handleScroll);
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false);
    }

    componentWillUnmount(): void {
        this.containerDiv.removeEventListener("scroll", this.handleScroll);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    changeSortColumn = (sortColumn: string) => {
        const state = this.state;
        const oldSortColumn = state.sortColumn;
        const sortOrderIsAsc = oldSortColumn === sortColumn ? !state.sortOrderIsAsc : true;
        this.searchItems(state.pageSize, 1, state.filter, sortColumn, sortOrderIsAsc, true, false);
    };

    getEditItem = (id: string) => {
        const promises = [
            salaryRowTypeService
                .getSalaryRowTypeEdit(id)
                .then((edit) => {
                    this.setState({
                        edit: edit,
                    });
                })
                .catch(console.error)
        ];

        Promise.all(promises)
            .then(() =>
                this.setState({
                    showEditDialog: true,
                })
            )
            .catch(console.error);
    };

    handleAdd = () => {
        this.getEditItem(Base.emptyGuid);
    };

    handleEdit = () => {
        const selectedId = this.state.selectedId;
        if (!selectedId) return;
        this.getEditItem(selectedId);
    };

    handleEditDialogOk = () => {
        this.setState({
            showEditDialog: false
        });
        this.refreshList();
    };

    handleEditDialogCancel = () => {
        this.setState({
            showEditDialog: false
        });
    };

    handleRemove = () => {
        const obj = this;
        const state = this.state;
        const selectedId = state.selectedId;
        if (!selectedId) return;
        const item = state.items.find(i => i.id === selectedId);
        if (!item) return;
        store.customStore.dispatch(StoreActions.setConfirmation(ConfirmationDialogType.Warning, t("common.warning"), String.format(t("companySettings.salaryRowTypeRemoveConfirmation"), item.name), () => {
            store.customStore.dispatch(StoreActions.clearConfirmation());
            // Call server
            store.customStore.dispatch(StoreActions.fetchStart());
            salaryRowTypeService.removeSalaryRowTypes([selectedId])
                .then(success => {
                    store.customStore.dispatch(StoreActions.showSuccessMessage(success.message));
                    obj.refreshList(); // TODO UPDATE ONLY CHANGED CUSTOMER DATA
                })
                .catch(error => {
                    store.customStore.dispatch(StoreActions.showErrorMessage(baseService.getErrorMessageFromError(error)));
                    return null;
                })
                .finally(() => store.customStore.dispatch(StoreActions.fetchEnd()));
        }, () => {
            store.customStore.dispatch(StoreActions.clearConfirmation());
        }));
    };

    handleClick = (id: string) => {
        this.setState({
            selectedId: id
        });
    };

    handleDoubleClick = (id: string) => {
        this.getEditItem(id);
    };

    render() {
        const state = this.state;
        const items = state.items;
        return (
            <div>
                <div className="row commandRow main">
                    <ToolTitle
                        title={t("companySettings.salaryRowTypes")}
                    />
                    <div className="col">
                        <ToolButton
                            title={t("common.add")}
                            enabled={true}
                            classes={"round left add"}
                            onClick={this.handleAdd}
                        />
                        <ToolButton
                            title={t("common.edit")}
                            enabled={!!state.selectedId}
                            classes={"round left edit"}
                            onClick={this.handleEdit}
                        />
                        <ToolButton
                            title={t("common.remove")}
                            enabled={!!state.selectedId}
                            classes={"round left remove"}
                            onClick={this.handleRemove}
                        />
                    </div>
                    <div className="col-auto right">
                        <ListSearchFilter
                            searchFilter={this.state.filter}
                            onSearchClick={this.changeFilter}
                        />
                    </div>
                </div>
                <div>
                    <OwnerSettingsSalaryRowTypeListLineHeader
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        onColumnClick={this.changeSortColumn}
                    />
                </div>
                <div className="listContainer salaryRowTypesContainer">
                    <div className="list striped" ref={(elem) => { this.containerDiv = elem; }}>
                        <div className="lineContainer" ref={(elem) => { this.listDiv = elem; }}>
                            {items.map((item) =>
                                <OwnerSettingsSalaryRowTypeListLine
                                    key={item.id}
                                    item={item}
                                    selectedId={state.selectedId}
                                    onClick={this.handleClick}
                                    onDoubleClick={this.handleDoubleClick}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {state.showEditDialog &&
                    <OwnerSettingsSalaryRowTypeDialog
                        edit={state.edit}
                        onOk={this.handleEditDialogOk}
                        onCancel={this.handleEditDialogCancel}
                    />
                }
            </div>
        );
    }
}
